// @ts-nocheck — browser module; window globals & untyped DOM refs are expected
import {
  isLoggedIn, getToken, logout, onAuthChange,
} from './shared/auth.js';
import { initAuthUI, processOAuthCallback } from './shared/auth-ui.js';
import {
  priceIdForTier,
  renderTierCards as renderTierCardsShared,
  tierBadgeClass,
  tierDisplayName,
} from './shared/tiers.js';
import {
  applyPurchaseOrder,
  packBalanceLine,
  packCardNote,
  packPriceId,
  renderPackCards as renderPackCardsShared,
} from './shared/packs.js';
import { BILLING_MANAGE_LABEL, billingView, portalDeepLinkRequested } from './shared/billing.js';
import {
  emitPortalReconcile,
  emitPricingView,
  emitUpgradeIntent,
  UPGRADE_SURFACE_BILLING,
} from './shared/analytics.js';
import { showToast as showToastShared } from './shared/toast.js';
import { apiRequest, configureClient } from './shared/api.js';

// /billing is a web page on the app domain, so it identifies as the SPA's
// surface (docs/constraints/client-identity.md).
configureClient('web', window.PODSTOW_CLIENT_VERSION || 'dev');

// ---------------------------------------------------------------------------
// Config (injected at deploy time via config.js)
// ---------------------------------------------------------------------------
const STRIPE_PK = window.PODSTOW_STRIPE_PUBLISHABLE_KEY ?? '';

// ---------------------------------------------------------------------------
// DOM refs — grabbed on DOMContentLoaded; tests inject them via _initDomRefs.
// ---------------------------------------------------------------------------
let $signinCard, $logoutBtn, $loading,
    $planCard, $currentTier, $manageBilling,
    $tiersCard, $tiers, $planChangeNote,
    $packsCard, $packs, $packsNote, $packsBalance,
    $packsAutoOptIn,
    $checkoutCard,
    $successCard, $successDone, $successHeading, $successText,
    $appleBilledNote, $appleBilledManageLink, $googleBilledNote, $googleBilledManageLink,
    $pendingChangeNotice;

function grabDomRefs() {
  $signinCard    = document.getElementById('signin-card');
  $logoutBtn     = document.getElementById('logout-btn');
  $loading       = document.getElementById('loading');
  $planCard      = document.getElementById('plan-card');
  $currentTier   = document.getElementById('current-tier');
  $manageBilling = document.getElementById('manage-billing-btn');
  $tiersCard     = document.getElementById('tiers-card');
  $tiers         = document.getElementById('tiers');
  $planChangeNote = document.getElementById('plan-change-note');
  $packsCard     = document.getElementById('packs-card');
  $packs         = document.getElementById('packs');
  $packsNote     = document.getElementById('packs-note');
  $packsBalance  = document.getElementById('packs-balance');
  $packsAutoOptIn = document.getElementById('packs-auto-opt-in');
  $checkoutCard  = document.getElementById('checkout-card');
  $successCard   = document.getElementById('success-card');
  $successDone   = document.getElementById('success-done-btn');
  $successHeading = document.getElementById('success-heading');
  $successText   = document.getElementById('success-text');
  $appleBilledNote       = document.getElementById('apple-billed-note');
  $pendingChangeNotice   = document.getElementById('pending-change-notice');
  $appleBilledManageLink = document.getElementById('apple-billed-manage-link');
  $googleBilledNote      = document.getElementById('google-billed-note');
  $googleBilledManageLink = document.getElementById('google-billed-manage-link');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Billing keeps slightly longer-lived toasts than the shared 3500ms default —
// checkout/portal errors carry more text.
const showToast = (msg, type) => showToastShared(msg, type, 4000);

function hideAll() {
  $signinCard.hidden = true;
  $loading.hidden = true;
  $planCard.hidden = true;
  $tiersCard.hidden = true;
  if ($packsCard) $packsCard.hidden = true;
  $checkoutCard.hidden = true;
  $successCard.hidden = true;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------
/**
 * One API request through the shared path, which stamps X-Podstow-Client.
 * No retries, as before the move: the POSTs mint sessions and carry no
 * Idempotency-Key. apiRequest throws on a non-2xx answer after reading its
 * body, so that answer is rebuilt as a Response; a network failure still
 * throws.
 */
async function billingRequest(path, init) {
  try {
    return await apiRequest(path, init, { retry: false });
  } catch (err) {
    if (typeof err?.status !== 'number') throw err;
    return new Response(err.body ?? '', { status: err.status });
  }
}

async function fetchAccountData() {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await billingRequest('/api/me', {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function createCheckoutSession(priceId) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await billingRequest('/api/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ priceId }),
  });

  if (!res.ok) {
    const body = await res.text();
    // Both 409s are written for the user — each names the door that works
    // (the portal, #3024; Apple, ADR #2867 §1) — so toast them verbatim.
    // Anything without a message keeps the diagnostic form.
    let message = '';
    try { message = String(JSON.parse(body)?.error ?? ''); } catch { /* not JSON */ }
    throw new Error(message || `Checkout error: ${res.status} ${body}`);
  }
  return res.json();
}

// The pack door (#3272). Its own endpoint because it refuses on eligibility,
// not subscription state: a subscribed account here is buying articles.
async function createPackCheckoutSession(priceId, { saveForAutoTopUp = false } = {}) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await billingRequest('/api/create-pack-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    // Only when the buyer asked: `setup_future_usage` on the session is what
    // keeps the card for a later off-session charge, and a pack bought is not
    // an agreement to be charged again (#3273).
    body: JSON.stringify({ priceId, ...(saveForAutoTopUp ? { saveForAutoTopUp: true } : {}) }),
  });

  if (!res.ok) {
    const body = await res.text();
    // The ineligible-pack 403 is written for the user; toast it verbatim.
    let message = '';
    try { message = String(JSON.parse(body)?.error ?? ''); } catch { /* not JSON */ }
    throw new Error(message || `Checkout error: ${res.status} ${body}`);
  }
  return res.json();
}

async function createPortalSession() {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await billingRequest('/api/create-portal-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: '{}',
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Portal error: ${res.status} ${body}`);
  }
  return res.json();
}

// Reconcile from Stripe by the return URL's checkout session id. The
// `customer.subscription.created` webhook races this redirect, so without it
// the next `/me` can still show the old tier.
async function confirmSubscription(sessionId) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await billingRequest('/api/confirm-subscription', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sessionId }),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Confirm error: ${res.status} ${body}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------
// /billing is a plain web page wherever it is opened: store shells never link
// to it (docs/constraints/billing-store-cards.md), so it states no host fact.
const SURFACE = { app: 'billing' };

// Paint a store note's parts into its element. The link part reuses the
// markup's anchor, so it stays one element across renders.
function renderNote($note, $link, parts) {
  if (!$note) return;
  const nodes = parts.map((part) => {
    if (typeof part === 'string') return document.createTextNode(part);
    if (!$link) return document.createTextNode(part.text);
    $link.textContent = part.text;
    $link.href = part.href;
    return $link;
  });
  $note.replaceChildren(...nodes);
}

function renderAccountDashboard(data) {
  hideAll();

  const tier = data.tier ?? 'free';
  $currentTier.textContent = '';
  const badge = document.createElement('span');
  badge.className = tierBadgeClass(tier);
  badge.textContent = tierDisplayName(tier);
  $currentTier.appendChild(badge);

  // Everything here besides the badge is a Stripe surface; billingView decides
  // who gets which door, note and route (shared/billing.js).
  const view = billingView(data, SURFACE);

  // The button opens the Stripe portal; a store's route is its note's link.
  $manageBilling.hidden = view.manage?.route !== 'portal';

  // A store-billed account gets its store's note instead of the Stripe
  // surface (ADR #2867 §5, Play since #3043).
  const noteBiller = view.note?.biller ?? null;
  if ($appleBilledNote) {
    $appleBilledNote.hidden = noteBiller !== 'apple';
    if (noteBiller === 'apple') renderNote($appleBilledNote, $appleBilledManageLink, view.note.parts);
  }
  if ($googleBilledNote) {
    $googleBilledNote.hidden = noteBiller !== 'google';
    if (noteBiller === 'google') renderNote($googleBilledNote, $googleBilledManageLink, view.note.parts);
  }

  // Pending plan change (#3029), as /settings renders it. Not gated on
  // provider: an Apple-billed account sees it under the Apple note.
  if ($pendingChangeNotice) {
    $pendingChangeNotice.textContent = view.pendingNotice ?? '';
    $pendingChangeNotice.hidden = view.pendingNotice === null;
  }

  $planCard.hidden = false;

  if (view.planDoor === 'none') {
    $tiersCard.hidden = true;
  } else {
    renderTierCards(tier, view);
  }

  // Packs sell to store-billed accounts too (ADR #3270): the credit is
  // provider-neutral and grants no entitlement.
  renderPackCards(tier, data);

  $logoutBtn.hidden = false;
}

function renderTierCards(currentTier, view) {
  // A live Stripe subscription makes the cards a portal-routed plan picker
  // rather than a checkout entry (#3024).
  const portalRouted = view.planDoor === 'portal';
  const rendered = renderTierCardsShared({
    container: $tiers,
    currentTier,
    portalRouted,
    cancelled: view.cancelled,
    onUpgrade: (tier, priceId) => {
      // The picker click is the ask (#2914); the ?upgrade= deep link is not.
      emitUpgradeIntent({
        surface: UPGRADE_SURFACE_BILLING,
        tier: currentTier,
        targetTier: tier.id,
      });
      startCheckout(priceId);
    },
    // A live subscription changes plan at the portal (#3024). No intent emit:
    // the funnel ends at `stripe-checkout-completed`, which a portal change
    // never produces.
    onChangePlan: () => openBillingPortal(),
    // Grace window: renewing is the portal's job — a checkout would open a
    // second subscription and forfeit the paid remainder. No intent emit.
    onResubscribe: () => openBillingPortal(),
  });
  if (rendered === 0) {
    // Nothing to offer, or PODSTOW_TIERS wasn't injected.
    $tiersCard.hidden = true;
    return;
  }
  $tiersCard.hidden = false;
  // When a change takes effect (ADR #3181), plus the one-way warning for a
  // grandfathered plan. Only for a live subscriber: an account starting a
  // subscription has no change to time.
  if ($planChangeNote) {
    $planChangeNote.textContent = view.planChangeNote ?? '';
    $planChangeNote.hidden = view.planChangeNote === null;
  }
  // The funnel top (#2914), only for cards that reach a checkout. The helper
  // dedupes per page load, so a re-render after a failed checkout is free.
  if (view.funnelTop) emitPricingView({ surface: UPGRADE_SURFACE_BILLING, tier: currentTier });
}

// Article packs (#3272), ordered against the plan cards by shared/packs.js
// (ADR #3591) so /settings cannot order them differently.
function renderPackCards(currentTier, data) {
  if (!$packsCard) return;
  const rendered = renderPackCardsShared({
    container: $packs,
    currentTier,
    onBuy: (pack, priceId) => startPackCheckout(priceId ?? packPriceId(pack.id)),
  });
  if (rendered === 0) {
    // No pack offered to this tier (ADR #3270), or no pack config injected.
    $packsCard.hidden = true;
    return;
  }
  $packsCard.hidden = false;
  if ($packsNote) $packsNote.textContent = packCardNote(currentTier);
  if ($packsBalance) {
    const line = packBalanceLine(data?.overage);
    $packsBalance.textContent = line;
    $packsBalance.hidden = line === '';
  }
  applyPurchaseOrder($packsCard, $tiersCard, currentTier);
}

// ---------------------------------------------------------------------------
// Stripe Checkout
// ---------------------------------------------------------------------------
let stripePromise = null;

function loadStripe() {
  if (!stripePromise) {
    stripePromise = (async () => {
      if (!window.Stripe) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/v3/';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Stripe.js'));
          document.head.appendChild(script);
        });
      }
      return window.Stripe(STRIPE_PK);
    })();
  }
  return stripePromise;
}

async function startCheckout(priceId) {
  try {
    hideAll();
    $loading.hidden = false;
    $loading.textContent = 'Preparing checkout...';

    const { clientSecret } = await createCheckoutSession(priceId);
    const stripe = await loadStripe();

    hideAll();
    $checkoutCard.hidden = false;

    const checkout = await stripe.createEmbeddedCheckoutPage({ clientSecret });
    checkout.mount('#checkout');
  } catch (err) {
    console.error('Checkout error:', err);
    showToast(err.message || 'Failed to start checkout', 'error');
    await loadAccount();
  }
}

// The pack purchase: the same embedded Checkout, on a `mode: 'payment'`
// session.
async function startPackCheckout(priceId, { saveForAutoTopUp } = {}) {
  if (!priceId) {
    showToast('That pack isn\'t available yet', 'error');
    return;
  }
  try {
    hideAll();
    $loading.hidden = false;
    $loading.textContent = 'Preparing checkout...';

    // The buyer's consent to a later off-session charge (#3273): from the
    // deep link when it carried one (no cards are painted then), else the
    // checkbox.
    const { clientSecret } = await createPackCheckoutSession(priceId, {
      saveForAutoTopUp: saveForAutoTopUp ?? $packsAutoOptIn?.checked === true,
    });
    const stripe = await loadStripe();

    hideAll();
    $checkoutCard.hidden = false;

    const checkout = await stripe.createEmbeddedCheckoutPage({ clientSecret });
    checkout.mount('#checkout');
  } catch (err) {
    console.error('Pack checkout error:', err);
    showToast(err.message || 'Failed to start checkout', 'error');
    await loadAccount();
  }
}

// ---------------------------------------------------------------------------
// Manage billing
// ---------------------------------------------------------------------------
// Set once the portal redirect is in motion: a back-forward cache restore
// after it is a return from the portal (handlePageShow).
let portalRedirectStarted = false;

// Restores the button's label (index.html's, and BILLING_MANAGE_LABEL) after a
// failed open and after a back-forward cache restore, since
// renderAccountDashboard only toggles it.
function resetManageBillingButton() {
  $manageBilling.disabled = false;
  $manageBilling.textContent = BILLING_MANAGE_LABEL;
}

// Resolves true once the redirect to the portal has been set in motion,
// false on failure (already toasted) — so callers with no dashboard on
// screen yet (the ?manage= deep-link) know to paint one.
async function openBillingPortal() {
  try {
    $manageBilling.disabled = true;
    $manageBilling.textContent = 'Opening...';
    const { url } = await createPortalSession();
    portalRedirectStarted = true;
    window.location.href = url;
    return true;
  } catch (err) {
    console.error('Portal error:', err);
    showToast(err.message || 'Failed to open billing portal', 'error');
    resetManageBillingButton();
    return false;
  }
}

// ---------------------------------------------------------------------------
// Main flow
// ---------------------------------------------------------------------------
async function loadAccount() {
  hideAll();
  $loading.hidden = false;
  $loading.textContent = 'Loading your account...';

  try {
    const data = await fetchAccountData();
    renderAccountDashboard(data);
  } catch (err) {
    console.error('Failed to load account:', err);
    showToast('Failed to load account data', 'error');
    hideAll();
    $signinCard.hidden = false;
  }
}

function showSignin() {
  hideAll();
  $signinCard.hidden = false;
  $logoutBtn.hidden = true;
}

// The subscription card's copy lives in the markup; remembered on first use.
let _successSubscriptionHeading = null;
let _successSubscriptionText = null;

// Checkout return (?session_id=).
async function handleReturn() {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  if (!sessionId) return false;

  // A pack return is marked by its door (#3272). A payment-mode session has
  // no subscription to confirm; the webhook credits the pack.
  const isPack = params.get('pack_purchase') !== null;

  window.history.replaceState({}, '', window.location.pathname);

  // Non-fatal: the webhook is the backstop.
  hideAll();
  $loading.hidden = false;
  $loading.textContent = isPack ? 'Finalising your purchase...' : 'Finalising your subscription...';
  if (!isPack) {
    try {
      await confirmSubscription(sessionId);
    } catch (err) {
      console.error('Confirm subscription error:', err);
    }
  }

  hideAll();
  // The heading swaps too: "Payment Successful" over a pack payment that may
  // be outstanding makes the promise the body copy stopped making (#3425).
  if ($successHeading) {
    _successSubscriptionHeading ??= $successHeading.textContent;
    $successHeading.textContent = isPack ? PACK_RETURN_HEADING : _successSubscriptionHeading;
  }
  if ($successText) {
    _successSubscriptionText ??= $successText.textContent;
    $successText.textContent = isPack ? PACK_RETURN_TEXT : _successSubscriptionText;
  }
  $successCard.hidden = false;
  return true;
}

// The pack return's copy (#3425), true of every payment method: fulfillment
// gates on `payment_status === 'paid'` (#3414), and a delayed method, enabled
// at the Stripe dashboard with no deploy, settles days later or never. A
// per-return status read to pick between two strings was built and dropped.
// "Payment Successful" stays the subscription return's: that checkout settles
// inside the session.
const PACK_RETURN_HEADING = 'Purchase Received';
const PACK_RETURN_TEXT =
  'Your article pack lands in your balance as soon as your payment is confirmed — '
  + 'normally within moments, though some payment methods take a few days.';

// ---------------------------------------------------------------------------
// Portal return — ?portal=return (src/billing/portal-session.ts's return_url), or a
// back-forward cache restore after the portal redirect (handlePageShow). The
// webhook for what the user did in the portal can lag it, so the return
// reconciles from Stripe first, then renders, then re-reads once after a
// delay as the net under a failed or stale reconcile. The re-read fails
// silently: a stale dashboard beats loadAccount's sign-in card.
// ---------------------------------------------------------------------------
const PORTAL_RETURN_REFRESH_DELAY_MS = 4000;
let portalReturnRefreshDelayMs = PORTAL_RETURN_REFRESH_DELAY_MS;

// Best-effort and never throws; the outcome is reported (#3199, values on
// emitPortalReconcile in analytics.js) but not branched on.
async function reconcileSubscription() {
  // Kept only when no response arrived: getToken() answers null rather than
  // throwing, so a token problem takes `skipped` instead.
  let report = { outcome: 'unreachable' };
  try {
    const token = await getToken();
    if (!token) {
      // Not signed out (init() checked isLoggedIn()), so the refresh failed:
      // a real failure, and loadAccount() then shows the sign-in card.
      report = { outcome: 'skipped' };
    } else {
      const res = await billingRequest('/api/reconcile-subscription', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      report = await readReconcileOutcome(res);
    }
  } catch {
    // The follow-up read still runs.
  }
  // Guarded here rather than trusting emitEvent: reconcileThenRender awaits
  // this bare, and a throw would cost the render and the re-read.
  try {
    emitPortalReconcile(report);
  } catch { /* analytics is best-effort — never at the render's expense */ }
}

/**
 * Classify the reconcile's `{ reconciled, provider, state }` answer. Never
 * throws: an unparseable body is `unreadable`, not a network failure.
 */
async function readReconcileOutcome(res) {
  if (!res.ok) return { outcome: 'error', status: res.status };
  try {
    const body = await res.json();
    return {
      outcome: body?.reconciled ? 'reconciled' : 'declined',
      status: res.status,
      provider: body?.provider,
      state: body?.state,
    };
  } catch {
    return { outcome: 'unreadable', status: res.status };
  }
}

async function handlePortalReturn() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('portal') !== 'return') return false;

  window.history.replaceState({}, '', window.location.pathname);
  await reconcileThenRender();
  return true;
}

// Back out of the portal instead of its return link, and the browser may
// restore /billing/ from the back-forward cache exactly as it was left: a
// disabled "Opening..." button, or the ?manage= deep link's lone loading line,
// with no reconcile behind it (#4687). A restore after a portal redirect is a
// portal return, so it takes the same path. Any other restore is the page the
// user left, and reports no reconcile.
async function handlePageShow(event) {
  if (!event.persisted || !portalRedirectStarted) return false;
  portalRedirectStarted = false;
  resetManageBillingButton();
  await reconcileThenRender();
  return true;
}

// Bumped by each return. A follow-up read scheduled by an earlier return can
// be frozen in the back-forward cache and resume beside a restore's own; only
// the newest return's may read or paint, or a pre-reconcile /me could land
// over the restore's render.
let portalReturnGeneration = 0;

async function reconcileThenRender() {
  const generation = ++portalReturnGeneration;
  await reconcileSubscription();
  await loadAccount();

  setTimeout(async () => {
    // Repaint only while the dashboard (the plan card) is on screen and no
    // newer return has started: a repaint would wipe a checkout the user
    // opened in the meantime.
    if ($planCard.hidden || generation !== portalReturnGeneration) return;
    try {
      const data = await fetchAccountData();
      // re-check: the fetch races clicks, and a newer return, too
      if ($planCard.hidden || generation !== portalReturnGeneration) return;
      renderAccountDashboard(data);
    } catch { /* silent — see the section comment */ }
  }, portalReturnRefreshDelayMs);
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
async function init() {
  initAuthUI({
    container: $signinCard,
    showToast,
  });

  // Registered before the OAuth callback is processed.
  onAuthChange(() => {
    if (isLoggedIn()) {
      loadAccount();
    } else {
      showSignin();
    }
  });

  await processOAuthCallback();

  if (!isLoggedIn()) {
    showSignin();
    return;
  }

  if (await handleReturn()) return;
  if (await handlePortalReturn()) return;

  // ?upgrade=<tierId> from /settings' upgrade cards: straight into checkout.
  const params = new URLSearchParams(window.location.search);
  const upgradeTierId = params.get('upgrade');
  if (upgradeTierId) {
    window.history.replaceState({}, '', window.location.pathname);
    if (await handleUpgradeDeepLink(upgradeTierId)) return;
    // Unknown / unconfigured tier — fall through to the picker.
  }

  // ?pack=<packId> from /settings' pack cards (#3272). Only /billing carries
  // the price map, so the price is resolved here.
  const packId = params.get('pack');
  if (packId) {
    // The /settings checkbox's opt-in (#3273): this branch paints no cards,
    // so /billing's own checkbox is never there to ask.
    const saveForAutoTopUp = params.get('auto') === '1';
    window.history.replaceState({}, '', window.location.pathname);
    const priceId = packPriceId(packId);
    if (priceId) {
      await startPackCheckout(priceId, { saveForAutoTopUp });
      return;
    }
    // Unknown pack — fall through to the dashboard's cards.
  }

  // ?manage=1 from /settings' portal CTAs; the legacy ?resubscribe=1 still
  // sits in browser history.
  if (portalDeepLinkRequested(params)) {
    window.history.replaceState({}, '', window.location.pathname);
    if (await handleManageDeepLink()) return;
  }

  await loadAccount();
}

// Resolve ?upgrade=. Emits no `upgrade-intent`: the /settings click already
// recorded the ask. The URL is typeable and survives in history, so the
// account is read first and billingView says where it lands: a store-billed
// one on the dashboard note (ADR #2867 §1), a live Stripe subscriber at the
// portal (#3024), rather than in a checkout that 409s after the card is typed.
// Returns false when the tier has no configured price, so the caller shows
// the picker.
async function handleUpgradeDeepLink(upgradeTierId) {
  const priceId = priceIdForTier(upgradeTierId);
  if (!priceId) return false;

  let data;
  try {
    data = await fetchAccountData();
  } catch (err) {
    // Can't tell who bills the account: don't guess a checkout.
    console.error('Failed to load account before checkout:', err);
    return false;
  }
  const { upgrade } = billingView(data, SURFACE).deepLink;
  if (upgrade === 'dashboard') {
    renderAccountDashboard(data);
    return true;
  }
  if (upgrade === 'portal') return openPortalOrRenderDashboard(data);
  await startCheckout(priceId);
  return true;
}

// Portal redirect from a deep link, behind a loading line: a dashboard would
// report a pricing-view (#2914) for cards the user leaves at once. Only a
// failed portal open (a 400 for an account with no Stripe customer included)
// paints the dashboard. Always true: the deep link is resolved either way.
async function openPortalOrRenderDashboard(data) {
  hideAll();
  $loading.hidden = false;
  $loading.textContent = 'Opening the billing portal...';
  if (!(await openBillingPortal())) {
    renderAccountDashboard(data);
  }
  return true;
}

// Resolve ?manage=: open the Stripe portal (only /billing can mint a
// session). A store-billed account lands on the dashboard note instead, as
// billingView decides. Returns false only when the account cannot be read.
async function handleManageDeepLink() {
  let data;
  try {
    data = await fetchAccountData();
  } catch (err) {
    console.error('Failed to load account before portal:', err);
    return false;
  }
  if (billingView(data, SURFACE).deepLink.manage === 'dashboard') {
    renderAccountDashboard(data);
    return true;
  }
  return openPortalOrRenderDashboard(data);
}

// ---------------------------------------------------------------------------
// Boot. Tests import the module without DOMContentLoaded and inject refs via
// _initDomRefs.
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  grabDomRefs();
  $logoutBtn.addEventListener('click', () => logout());
  $manageBilling.addEventListener('click', () => openBillingPortal());
  $successDone.addEventListener('click', () => loadAccount());
  window.addEventListener('pageshow', (event) => handlePageShow(event));
  init();
});

// ---------------------------------------------------------------------------
// Test seams
// ---------------------------------------------------------------------------
export function _initDomRefs(refs) {
  // Fresh refs stand for a fresh page load, which has started no redirect.
  portalRedirectStarted = false;
  $signinCard    = refs.signinCard ?? null;
  $logoutBtn     = refs.logoutBtn ?? null;
  $loading       = refs.loading ?? null;
  $planCard      = refs.planCard ?? null;
  $currentTier   = refs.currentTier ?? null;
  $manageBilling = refs.manageBilling ?? null;
  $tiersCard     = refs.tiersCard ?? null;
  $tiers         = refs.tiers ?? null;
  $planChangeNote = refs.planChangeNote ?? null;
  $checkoutCard  = refs.checkoutCard ?? null;
  $successCard   = refs.successCard ?? null;
  $successDone   = refs.successDone ?? null;
  $successHeading = refs.successHeading ?? null;
  $successText   = refs.successText ?? null;
  $appleBilledNote       = refs.appleBilledNote ?? null;
  $pendingChangeNotice   = refs.pendingChangeNotice ?? null;
  $appleBilledManageLink = refs.appleBilledManageLink ?? null;
  $googleBilledNote      = refs.googleBilledNote ?? null;
  $googleBilledManageLink = refs.googleBilledManageLink ?? null;
  $packsCard     = refs.packsCard ?? null;
  $packs         = refs.packs ?? null;
  $packsNote     = refs.packsNote ?? null;
  $packsBalance  = refs.packsBalance ?? null;
  $packsAutoOptIn = refs.packsAutoOptIn ?? null;
}

export {
  startPackCheckout as _startPackCheckout,
  renderAccountDashboard as _renderAccountDashboard,
  handleReturn as _handleReturn,
  handleUpgradeDeepLink as _handleUpgradeDeepLink,
  handleManageDeepLink as _handleManageDeepLink,
  handlePortalReturn as _handlePortalReturn,
  handlePageShow as _handlePageShow,
  openBillingPortal as _openBillingPortal,
};
/** Drop the memoised Stripe.js handle, so each test's `window.Stripe` stub is
 *  the one loadStripe() returns. */
export function _resetStripeLoader() {
  stripePromise = null;
}

export function _setPortalReturnRefreshDelay(delayMs) {
  portalReturnRefreshDelayMs = delayMs ?? PORTAL_RETURN_REFRESH_DELAY_MS;
}
